import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * ProtectedRoute - Requires authentication to access
 * Redirects to login page if not authenticated
 */
export function ProtectedRoute({ children }) {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    // Show nothing while checking auth status
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    // Redirect to login if not authenticated
    if (!user) {
        // Save the attempted URL for redirecting after login
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    // Festival guests can only ever see their own competition page
    if (user.festival_guest && location.pathname !== `/competitions/${user.restricted_comp_id}`) {
        return <Navigate to={`/competitions/${user.restricted_comp_id}`} replace />;
    }

    return children;
}

/**
 * AdminRoute - Requires admin role to access
 * Redirects to home if not admin
 */
export function AdminRoute({ children }) {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    if (!user.is_admin) {
        return <Navigate to="/" replace />;
    }

    return children;
}

/**
 * PublicOnlyRoute - Only accessible when NOT logged in
 * Redirects to home if already authenticated
 */
export function PublicOnlyRoute({ children }) {
    const { user, isLoading } = useAuth();
    const location = useLocation();

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    // If logged in, redirect to home or the page they came from
    if (user) {
        if (user.festival_guest) {
            return <Navigate to={`/competitions/${user.restricted_comp_id}`} replace />;
        }
        const from = location.state?.from?.pathname || '/';
        return <Navigate to={from} replace />;
    }

    return children;
}
